import { ExperienceDateService } from '@app/onboarding/services/experience-date.service';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { TagsApi } from '@app/tags/tag-api.model';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { UserPosition } from '@app/inputs/inputs-api.model';
import { InputModalDispatcherService } from '@app/user-content/user-input/services/input-modal-dispatcher.service';
import { ExperienceItem } from '@app/profile/components/profile-overview/experiences-v2/profile-experience.facade';
import { ReactiveListVM } from '@dg/shared-rsm';
import { ProfileOverviewFacade } from '@app/profile/components/profile-overview-v2/profile-overview.facade';
import { ProfileOverviewViewModel } from '@app/profile/components/profile-overview-v2/profile-overview.model';
import { AuthService, LDFlagsService } from '@dg/shared-services';
import { ProfileSkillSignalsPageViewModel } from '@app/profile/components/profile-skill-signals-page-v2/profile-skill-signals.model';
import { ProfileSkillSignalsPageFacade } from '@app/profile/components/profile-skill-signals-page-v2/profile-skill-signals-page.facade';

@Component({
  selector: 'dgx-profile-experiences-v2',
  templateUrl: './profile-experience.component.html',
  styleUrls: ['./profile-experience.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileExperienceComponentV2 extends SubscriberBaseDirective {
  @Input() public profileName: string;
  @Input() public ownerIsViewing: boolean;
  @Input() public isRestrictedView: boolean; // hides viewAll and add buttons in the skill signals page
  @Input() public experiences: ReactiveListVM<ExperienceItem>;
  @Input() public isPageLoading: boolean;

  @Input() public useLoadingTemplate: boolean = false; // used by skill signals
  @Input() public useShowMore: boolean; // used by skill signals
  @Input() public pageType: string; // overview, full experiences page or all signals page

  @Output() public loadMoreExperiences = new EventEmitter<void>();

  public viewAllUrl: string;
  public viewAllUrlQueryParams: { classifier: string };
  public isUserLoggedIn = !!this.authService?.authUser;
  public isOpen: { [id: string]: boolean } = {};
  public skillLimit: number = 20;

  public i18n = this.translateService.instant([
    'Core_Add',
    'Core_AddTags',
    'Core_ExperiencesDisplayName',
    'Core_ReportAProblem',
    'ProfileCtrl_ExperienceReported',
    'Core_Skills',
    'Core_Skill',
    'dgProfileOverview_AddExperiences',
    'dgProfileOverview_AddDescription',
    'Core_ShowMore',
    'A11y_ExpandExperience',
  ]);

  constructor(
    private translateService: TranslateService,
    private genericModalService: InputModalDispatcherService,
    private experienceDateService: ExperienceDateService,
    private authService: AuthService,
    private ldFlagsService: LDFlagsService
  ) {
    super();
  }

  public ngOnInit(): void {
    const ownerUrl = `/${this.profileName}`;
    this.viewAllUrl = `/profile${ownerUrl}/collection`;
    this.viewAllUrlQueryParams = { classifier: 'experiences' };
  }

  public get showExperienceTypes() {
    return this.ldFlagsService.showExperienceTypes;
  }

  /**
   * Determines if the details toggle should appear
   *
   * @param experience - the experience object.
   */
  public canToggleDetailsSection(experience: UserPosition): boolean {
    return !!(
      this.ownerIsViewing ||
      (!this.ownerIsViewing &&
        (experience.description || experience.tags?.length))
    );
  }

  /**
   * Determines if the add description link should appear
   *
   * @param description - the description of the experience
   */
  public showAddDescription(description: string): boolean {
    return !description && this.ownerIsViewing;
  }

  /**
   * Determines if the add skills link should appear
   *
   * @param skills - the description of the experience
   */
  public showAddSkills(skills: TagsApi.Tag[]): boolean {
    return !skills?.length && this.ownerIsViewing;
  }

  /**
   * Create a list of all the visible skills
   *
   * @param tags - the list of tags.
   */
  public getVisibleSkills(tags: TagsApi.Tag[]): TagsApi.Tag[] {
    return tags.slice(0, this.skillLimit);
  }

  /**
   * Create a label with a total count of the number of skills
   *
   * @param tags - the list of tags.
   */
  public getPillSkillCount(tags: TagsApi.Tag[]): string {
    const plural =
      tags.length === 1 ? this.i18n.Core_Skill : this.i18n.Core_Skills;
    return `${tags.length} ${plural}`;
  }

  /**
   * Create a label with a count of the number of skills greater than the skillLimit value
   *
   * @param tags - the list of tags.
   */
  public getHiddenSkills(tags: TagsApi.Tag[]): string {
    return `+ ${tags.length - this.skillLimit}`;
  }

  /**
   * Toggle the details section
   *
   * @param id - the id of the experience.
   */
  public toggleDetails(id: number): void {
    this.isOpen[id] = !this.isOpen[id];
  }

  /**
   * Sets a label of the date duration of the experience and along with the months and years
   *
   * @param experience - the experience object.
   */
  public buildDate(experience: UserPosition): string {
    return this.experienceDateService.buildDate(experience);
  }

  /**
   * Returns a formatted string for organization name and date so that the ellipsis in the view
   * can truncate the text accordingly
   *
   * @param experience - the experience object.
   */
  public getSubHeading(experience: UserPosition): string {
    if (!experience.organizationName) return '';
    return `${experience.organizationName} \u2022 ${this.buildDate(
      experience
    )}`;
  }

  /**
   * Opens the edit experience modal
   *
   * @param experience - the experience object.
   */
  public editExperience(experience: UserPosition): void {
    this.genericModalService
      .edit({
        inputId: experience.inputId,
        userInputId: experience.userInputId,
        inputType: 'Position',
        initialModel: {
          inputId: experience.inputId,
          userInputId: experience.userInputId,
          inputType: 'Position',
        },
      })
      .pipe(this.takeUntilDestroyed())
      .subscribe();
  }

  /**
   * Opens the add experience modal
   */
  public addExperience(event: Event): void {
    this.genericModalService
      .complete({
        inputType: 'Position',
      })
      .pipe(this.takeUntilDestroyed())
      .subscribe();
  }

  public addExperienceWithId(experienceId: string): void {
    this.genericModalService
      .showModal({
        inputType: 'Position',
        initialModel: {
          inputSubType: experienceId,
        },
        isCompleting: true,
      })
      .pipe(this.takeUntilDestroyed())
      .subscribe((result) => {});
  }
}
